import {
  Button,
  Divider,
  Fade,
  Modal,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import { DynamicIcon } from 'src/components';
import { useConfirmationStore } from 'src/store/confirmation-modal';

export const CONFIRMATION_MODAL_BODY_PORTAL_ID = 'confirmation-modal-body-portal';

const ModalContainer = styled(Stack)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  alignItems: 'center',
  width: 'min(30rem, calc(100vw - 2rem))',
  minWidth: 'min(26.25rem, calc(100vw - 2rem))',
  backgroundColor: theme.palette.custom.OV.sys.background,
  border: '1px solid',
  borderRadius: theme.shape.borderRadius,
  borderColor: theme.palette.custom.OV.sys.outline,
  padding: theme.spacing(1, 1),
}));

export type ModalVariant =
  | 'normal'
  | 'plain'
  | 'delete'
  | 'visibilityOff'
  | 'visibilityOn'
  | 'yesNo'
  | 'code';

export const ConfirmationModal = () => {
  const theme = useTheme();
  const { isOpen, title, body, variant, closeConfirmation, returnConfirmationResult } =
    useConfirmationStore();

  const MODAL_PROPS_BY_VARIANT = useMemo(() => {
    return {
      normal: {
        iconProps: { name: 'star', filled: true },
        iconColor: theme.palette.custom.OV.sys.onSurfaceVariant,
        color: theme.palette.custom.OV.sys.onTertiaryContainer,
        confirmBackground: theme.palette.custom.OV.sys.tertiaryContainer,
      },
      plain: {
        iconProps: null,
        iconColor: theme.palette.custom.OV.sys.onSurfaceVariant,
        color: theme.palette.custom.OV.sys.onTertiaryContainer,
        confirmBackground: theme.palette.custom.OV.sys.tertiaryContainer,
      },
      visibilityOn: {
        iconProps: { name: 'visibility', filled: true },
        iconColor: theme.palette.custom.OV.sys.onSurfaceVariant,
        color: theme.palette.custom.OV.sys.onTertiaryContainer,
        confirmBackground: theme.palette.custom.OV.sys.tertiaryContainer,
      },
      visibilityOff: {
        iconProps: { name: 'visibility_off', filled: true },
        iconColor: theme.palette.custom.OV.sys.onSurfaceVariant,
        color: theme.palette.custom.OV.sys.onTertiaryContainer,
        confirmBackground: theme.palette.custom.OV.sys.tertiaryContainer,
      },
      delete: {
        iconProps: { name: 'delete' },
        iconColor: theme.palette.custom.OV.sys.error,
        color: theme.palette.custom.OV.sys.onErrorContainer,
        confirmBackground: theme.palette.custom.OV.sys.errorContainer,
      },
      yesNo: {
        iconProps: { name: 'help' },
        iconColor: theme.palette.custom.OV.sys.onSurfaceVariant,
        color: theme.palette.custom.OV.sys.onTertiaryContainer,
        confirmBackground: theme.palette.custom.OV.sys.tertiaryContainer,
      },
      code: {
        iconProps: { name: 'code' },
        iconColor: theme.palette.custom.OV.sys.onSurfaceVariant,
        color: theme.palette.custom.OV.sys.onTertiaryContainer,
        confirmBackground: theme.palette.custom.OV.sys.tertiaryContainer,
      },
    };
  }, [theme]);

  const { confirmButtonText, cancelButtonText } = useMemo(() => {
    switch (variant) {
      case 'yesNo':
        return {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        };
      default:
        return {
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
        };
    }
  }, [variant]);

  const { iconProps, iconColor, color, confirmBackground } = MODAL_PROPS_BY_VARIANT[variant];
  const isCodeVariant = variant === 'code';
  const isPlainVariant = variant === 'plain';

  return (
    <Modal
      open={isOpen}
      onClose={closeConfirmation}
      aria-labelledby="confirmation-modal-title"
      aria-describedby="confirmation-modal-description"
    >
      <Fade in={isOpen}>
        <ModalContainer data-testid="confirmation-modal">
          {iconProps ? (
            <DynamicIcon
              {...iconProps}
              weight={500}
              sx={{
                color: iconColor,
                width: '2rem',
                height: '2rem',
                justifyContent: 'center',
              }}
            />
          ) : null}
          <Typography data-testid="confirmation-modal-title" variant="h6" component="h2">
            {title}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              my: 2,
              whiteSpace: 'pre-wrap',
              ...(isCodeVariant
                ? {
                    fontFamily: 'monospace',
                    textAlign: 'left',
                    width: '100%',
                    maxWidth: '100%',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    maxHeight: '50vh',
                    overflowY: 'auto',
                  }
                : { textAlign: 'center' }),
            }}
          >
            {body}
          </Typography>
          <Stack id={CONFIRMATION_MODAL_BODY_PORTAL_ID} width="100%" />
          <Divider />
          {isPlainVariant ? (
            <Stack direction="row" mt={1} justifyContent="center" width="100%">
              <Button
                data-testid="confirmation-modal-ok"
                sx={{ backgroundColor: confirmBackground, color, px: 3 }}
                onClick={() => {
                  returnConfirmationResult(true);
                  closeConfirmation();
                }}
              >
                OK
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" mt={1} gap={1} justifyContent="end" width="100%">
              <Button
                data-testid="confirmation-modal-cancel"
                sx={{ color }}
                onClick={() => {
                  returnConfirmationResult(false);
                  closeConfirmation();
                }}
              >
                {cancelButtonText}
              </Button>
              <Button
                data-testid="confirmation-modal-confirm"
                sx={{ backgroundColor: confirmBackground, color }}
                onClick={() => {
                  returnConfirmationResult(true);
                  closeConfirmation();
                }}
              >
                {confirmButtonText}
              </Button>
            </Stack>
          )}
        </ModalContainer>
      </Fade>
    </Modal>
  );
};
